import { useAtomValue, useSetAtom } from 'jotai'
import { Building2, Check, ChevronsUpDown } from 'lucide-react'
import { useCallback, useMemo, useState } from 'react'

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar'

import { cn } from '@/lib/tailwind.utils'
import { BASE_API_URL } from '@app/app-constants'
import { toast } from '@app/components/ui/Toast/toast'
import { isLoadingAuthStateAtom } from '@app/state/authAtom'
import { availableClients, selectedClientAtom } from '@app/state/clientsAtom'
import { buildIdAtom } from '@app/state/layoutAtom'
import type { Client } from '@app/types'
import { useHomeNavigation } from '@app/modules/HomeV2/hooks/useHomeNavigation'

const ClientSwitcher = () => {
  const { isMobile } = useSidebar()
  const clientList = useAtomValue(availableClients)
  const selectedClient = useAtomValue(selectedClientAtom)
  const buildId = useAtomValue(buildIdAtom)
  const setIsLoadingAuth = useSetAtom(isLoadingAuthStateAtom)
  const { logos } = useHomeNavigation()
  const [open, setOpen] = useState(false)
  const [clientToConfirm, setClientToConfirm] = useState<Client | null>(null)

  const selectedClientLabel = useMemo(
    () => selectedClient?.display_name ?? 'Select organisation',
    [selectedClient?.display_name],
  )

  const selectedClientInitials = useMemo(
    () =>
      selectedClient?.display_name
        .split(' ')
        .map((word) => word[0])
        .join('')
        .slice(0, 2)
        .toUpperCase(),
    [selectedClient?.display_name],
  )

  const handleClientChange = useCallback(
    (client: Client) => {
      if (client.client_name === selectedClient?.client_name) return

      setOpen(false)
      setClientToConfirm(client)
    },
    [selectedClient?.client_name],
  )

  const confirmClientChange = useCallback(() => {
    if (!clientToConfirm) return

    setClientToConfirm(null)
    setIsLoadingAuth(true)

    toast({
      type: 'info',
      description: `Switching to ${clientToConfirm.display_name}...`,
      position: 'top-center',
    })

    const url = new URL('/auth/login', BASE_API_URL)
    url.searchParams.set('client_name', clientToConfirm.subdomain)
    if (buildId) url.searchParams.set('return_path', `/?build=${buildId}`)
    window.location.href = url.toString()
  }, [clientToConfirm, buildId, setIsLoadingAuth])

  if (!selectedClient) return null

  return (
    <>
      <SidebarMenu>
        <SidebarMenuItem>
          <Popover open={open} onOpenChange={setOpen}>
            {clientList.length > 0 ? (
              <TooltipProvider delayDuration={0}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <PopoverTrigger asChild>
                      <SidebarMenuButton
                        size="lg"
                        disabled={clientList.length === 0}
                        className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-foreground group "
                      >
                        <Avatar className="size-6  rounded-none ml-1">
                          <AvatarImage
                            src={logos.whiteSimple}
                            alt={selectedClient?.display_name}
                            className=""
                          />
                          <AvatarFallback>{selectedClientInitials}</AvatarFallback>
                        </Avatar>

                        <div className="grid flex-1 text-left text-sm leading-tight">
                          <span className="truncate font-medium">{selectedClientLabel}</span>
                        </div>

                        <ChevronsUpDown className="ml-auto size-4" />
                      </SidebarMenuButton>
                    </PopoverTrigger>
                  </TooltipTrigger>
                  <TooltipContent side="right" align="center">
                    Switch organisation
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : (
              <SidebarMenuButton size="lg" asChild>
                <div>
                  <Avatar className="size-8">
                    <AvatarImage src={logos.whiteSimple} alt={selectedClient.display_name} />
                    <AvatarFallback>{selectedClientInitials}</AvatarFallback>
                  </Avatar>
                  <div className="flex flex-col gap-0.5 leading-none">
                    <span className="font-medium">{selectedClient.display_name}</span>
                  </div>
                </div>
              </SidebarMenuButton>
            )}

            <PopoverContent
              align="start"
              side={isMobile ? 'bottom' : 'right'}
              sideOffset={16}
              className="w-[--radix-popover-trigger-width] min-w-72 p-0"
            >
              <Command className="bg-background">
                <div className="flex items-center border-b px-3 py-2">
                  <CommandInput placeholder="Search organisation..." className="h-10" />
                </div>

                <CommandList className="max-h-72 overflow-y-auto">
                  <CommandEmpty>No organisation found.</CommandEmpty>
                  <CommandGroup heading="Organisations">
                    {clientList.map((client) => {
                      const isActive = client.client_name === selectedClient.client_name

                      return (
                        <CommandItem
                          key={client.client_name}
                          value={`${client?.display_name} ${client.client_name}`}
                          disabled={isActive}
                          onSelect={() => handleClientChange(client)}
                          className="gap-2 text-foreground"
                        >
                          <Building2 className="size-4" />

                          <span className="flex-1 truncate text-current">
                            {client.display_name}
                          </span>

                          <Check
                            className={cn(
                              'size-4',
                              isActive ? 'opacity-100 text-green-400' : 'opacity-0',
                            )}
                          />
                        </CommandItem>
                      )
                    })}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </SidebarMenuItem>
      </SidebarMenu>
      <AlertDialog
        open={Boolean(clientToConfirm)}
        onOpenChange={(isOpen) => {
          if (!isOpen) setClientToConfirm(null)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Switch organisation?</AlertDialogTitle>
            <AlertDialogDescription>
              You are about to switch from <strong>{selectedClient.display_name}</strong> to{' '}
              <strong>{clientToConfirm?.display_name}</strong>. This will reload the app and sign
              you in to the selected organisation.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmClientChange}>Switch organisation</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

export default ClientSwitcher
